import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
} from '@mui/material';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Timer,
  MoreHorizontal,
} from 'lucide-react';

import { useAppSelector } from '@/store';
import { selectActiveTimers } from '@/store/slices/timerSlice';

interface MobileBottomNavProps {
  onMoreClick?: () => void;
}

/**
 * MobileBottomNav
 * Bottom navigation bar for mobile devices
 * Shows primary navigation items with timer indicator
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onMoreClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTimers = useAppSelector(selectActiveTimers);

  // Get current route for active state
  const getCurrentValue = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/tasks')) return 'tasks';
    if (path.startsWith('/projects')) return 'projects';
    if (path.startsWith('/timer')) return 'timer';
    return 'more';
  };

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    switch (newValue) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'timer':
        // Navigate to tasks with timer view or show timer
        navigate('/tasks');
        break;
      case 'more':
        // Trigger the more menu callback
        if (onMoreClick) {
          onMoreClick();
        }
        break;
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: { xs: 'block', md: 'none' },
        pb: 'env(safe-area-inset-bottom)', // iOS safe area
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            py: 1,
            px: 0.5,
            gap: 0.5,
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            mt: 0.5,
            '&.Mui-selected': {
              fontSize: '0.65rem',
              fontWeight: 600,
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Dashboard"
          value="dashboard"
          icon={<LayoutDashboard size={22} />}
        />
        <BottomNavigationAction
          label="Tasks"
          value="tasks"
          icon={<CheckSquare size={22} />}
        />
        <BottomNavigationAction
          label="Projects"
          value="projects"
          icon={<FolderKanban size={22} />}
        />
        <BottomNavigationAction
          label="Timer"
          value="timer"
          icon={
            <Badge
              badgeContent={activeTimers.length}
              color="success"
              max={9}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <Timer size={22} />
            </Badge>
          }
        />
        <BottomNavigationAction
          label="More"
          value="more"
          icon={<MoreHorizontal size={22} />}
        />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
